import productService, {
  areMountingRailsOfSpecificColorValid,
  getValidMountingRailColors,
} from '../../../../services/products';
import geometry from '../../../../scene/util/geometry';
import tacHelpers from '../../tacHelpers';
import constants from '../../../../settings/constants';
import { adjustMountingRailChainPos } from '../common';
import { ITEMS } from '../../../../constants';

/**
 * Divides sections into groups (a Map) in such a way that sections that are
 * top aligned with each other belong to the same group. Note that "skewed"
 * sections (left upright higher/lower than the right upright) will not be
 * included in any group.
 * @param {Array} sections The sections to be divided.
 * @param {object} tac The tac containing the sections and their uprights.
 * @returns {object} A Map with the groups' "top y" coordinates as keys
 *                   and arrays of grouped sections as values.
 */
function getTopAlignedSectionGroups(sections, tac) {
  const sectionGroupsVertical = new Map();

  sections.forEach(section => {
    const topmostUprights = section.uprights
      .map(upright => tacHelpers.getItem(tac, upright.itemid))
      .sort((a, b) => b.y - a.y)
      .slice(0, 2);

    if (
      topmostUprights[0].y + topmostUprights[0].height ===
        topmostUprights[1].y + topmostUprights[1].height &&
      topmostUprights[1].y + topmostUprights[1].height ===
        section.y + section.height
    ) {
      if (sectionGroupsVertical.get(section.y + section.height)) {
        sectionGroupsVertical.get(section.y + section.height).push(section);
      } else {
        sectionGroupsVertical.set(section.y + section.height, [section]);
      }
    }
  });

  return sectionGroupsVertical;
}

/**
 * Takes a map of "key -> section arrays" and further splits the groups
 * belonging to each key in such a way that each group only contain
 * horizontally coherent sections (no horizontal "holes").
 * @param {Map} sectionGroupsToSplit A Map with arrays of sections as values.
 * @returns {object} A Map contining the split groups with keys based on the
 *                   keys of the input Map, but with an appended comma-separated
 *                   counter index depending on which consecutive section group
 *                   the key denotes (e.g., old key "120" would result in the new
 *                   keys "120,0", "120,1", ..., "120,n".
 */
function splitIntoCoherentSectionGroups(sectionGroupsToSplit) {
  const coherentSectionGroups = new Map();

  sectionGroupsToSplit.forEach((sections, prevKey) => {
    let coherentGroupCounter = 0;
    let prevSection = null;
    sections
      .sort((a, b) => a.x - b.x)
      .forEach(section => {
        prevSection &&
          prevSection.x + prevSection.width !== section.x &&
          coherentGroupCounter++;
        const updatedKey = `${prevKey},${coherentGroupCounter}`;
        if (coherentSectionGroups.get(updatedKey)) {
          coherentSectionGroups.get(updatedKey).push(section);
        } else {
          coherentSectionGroups.set(updatedKey, [section]);
        }
        prevSection = section;
      });
  });

  return coherentSectionGroups;
}

/**
 * Calculates the width of a coherent (no "holes") group of sections.
 * The width also includes the "overshooting" part of the group's uprights.
 * @param {Array} sections The sections beloning to a coherent group.
 * @returns {number} The width of the group.
 */
function widthOfSectionGroup(sectionsInGroup) {
  const width =
    sectionsInGroup.reduce((accWidth, section) => accWidth + section.width, 0) +
    2 * (productService.getPostWidth() / 2);

  return width;
}

/**
 * Gets the rail product corresponding to a specific approximate length
 * (=length as given in the range constants, e.g., 600).
 * @param {number | string} approxLength The approximate length.
 * @returns {object} The corresponding rail product.
 */
function getRailProductByApproxLength(approxLength, color) {
  let product = null;
  const productId = constants.MOUNTING_RAILS[color][approxLength];
  if (productId) {
    product = productService.getProduct(productId) || null;
  }

  return product;
}

/**
 * Gets the corresponding precise length (e.g, 624) of a rail,
 * given an approximate length (=length as listed in the range
 * constants, e.g., 600).
 * @param {number | string} approxLength The approximate length.
 * @returns {number} The corresponding precise length.
 */
function getPreciseRailLength(approxLength, color) {
  const preciseLength =
    getRailProductByApproxLength(approxLength, color)?.width || null;

  return preciseLength;
}

/**
 * Calculates the total length of a number of rail lengths.
 * @param {Array} rails The individual lengths of the rails.
 * @returns {number} The total length of all the rails.
 */
function lengthOfRails(rails) {
  const length = rails.reduce(
    (totalLengthAcc, railLength) => totalLengthAcc + railLength,
    0
  );
  return length;
}

/**
 * Given available rail lengths, calculates the optimal rail lengths
 * to use in order to cover a specific distance in such a way that the
 * total length is long enough to cover the distance, yet there should
 * be as little overflow as possible.
 * @param {Array} availableLengths The available rail lengths to be
 *                                 used in the calculations.
 * @param {number} lengthToCover The distance that needs to covered.
 * @returns {Array} The optimal rail lengths to cover the distance.
 *                  The same length might occur more than once.
 */
function getOptimallyCoveringRails(availableLengths, lengthToCover) {
  let optimalRails = [];
  if (lengthToCover > 0) {
    const candidates = [];
    availableLengths.forEach(availableLength => {
      const theRest = getOptimallyCoveringRails(
        availableLengths,
        lengthToCover - availableLength
      );
      availableLength + lengthOfRails(theRest) >= lengthToCover &&
        candidates.push([availableLength, ...theRest]);
    });
    optimalRails = candidates.reduce(
      (acc, curr) => (lengthOfRails(curr) < lengthOfRails(acc) ? curr : acc),
      [Infinity]
    );
  }
  return optimalRails;
}

function getY(section, mountingRailHeight) {
  return section.y + section.height - mountingRailHeight / 2;
}

/**
 * @returns {number} Intermediate x pos. This is only valid if there are no other
 * mounting rails in the vicinity, otherwise it needs later adjusting.
 */
function getX(section) {
  return section.x - productService.getPostWidth() / 2;
}

/**
 * Takes a rail product and adds a position based on a starting section
 * and a provided horizontal offset.
 * @param {object} section The section from which the positioning starts.
 * @param {number} xOffset The horizontal offset from the start of the chain.
 * @param {object} railProduct The original, unpositioned rail product.
 */
function getPositionedMountingRail(section, xOffset, railProduct) {
  return {
    ...railProduct,
    x: getX(section) + xOffset,
    y: getY(section, railProduct.filter.height),
    z: 0,
  };
}

/**
 * Gets the rail product corresponding to a specific precise length
 * (e.g., 624).
 * @param {number | string} approxLength The approximate length.
 * @returns {object} The corresponding rail product.
 */
function getRailProductByPreciseLength(preciseLength, color) {
  let product = null;
  const matchingProducts = Object.values(constants.MOUNTING_RAILS[color])
    .map(productId => productService.getProduct(productId))
    .filter(
      product =>
        product &&
        product.width &&
        product.width.toString() === preciseLength.toString()
    );
  matchingProducts.length && (product = matchingProducts[0]);

  return product;
}

function getCorrectlyPositionedMountingRails(mountingRailChain, wallWidth) {
  const chainCopy = mountingRailChain.slice();
  chainCopy.sort((a, b) => a.x - b.x);
  const newX = chainCopy[0].x;
  adjustMountingRailChainPos(chainCopy, newX, wallWidth);

  return chainCopy;
}

function decideColorOfMountingRailGroup(sectionsInGroup) {
  const uprights = sectionsInGroup.reduce((uprightsThisFar, section) => {
    const updatedUprightsThisFar = [...uprightsThisFar];
    section.uprights.forEach(uprightInSection => {
      if (
        !updatedUprightsThisFar.find(
          upright => upright.itemid === uprightInSection.itemid
        )
      )
        updatedUprightsThisFar.push(uprightInSection);
    });
    return updatedUprightsThisFar;
  }, []);
  const colorsOfUprights = uprights.map(upright => upright.filter.color);
  const countedColors = colorsOfUprights.reduce((resultThisFar, color) => {
    const updatedResultThisFar = { ...resultThisFar };
    updatedResultThisFar[color] = resultThisFar[color]
      ? resultThisFar[color] + 1
      : 1;
    return updatedResultThisFar;
  }, {});
  const colorsSortedDescending = Object.keys(countedColors).sort(
    (color1, color2) => countedColors[color2] - countedColors[color1]
  );
  const chosenColor =
    colorsSortedDescending.find(color =>
      areMountingRailsOfSpecificColorValid(color)
    ) || getValidMountingRailColors()[0];

  return chosenColor;
}

function getGroupedMountingRails(tac) {
  const groupedMountingRails = [];

  const postWidth = productService.getPostWidth();

  const mountingSections = tac.items.filter(
    item =>
      item.type === ITEMS.SECTION && !item.keepDuringDrag && item.width < 1000
  );

  const mountingSectionGroupsVertical = getTopAlignedSectionGroups(
    mountingSections,
    tac
  );
  const mountingSectionGroups = splitIntoCoherentSectionGroups(
    mountingSectionGroupsVertical
  );

  mountingSectionGroups.forEach(sectionsInGroup => {
    const color = decideColorOfMountingRailGroup(sectionsInGroup);
    const groupWidth = widthOfSectionGroup(sectionsInGroup);

    const preciseRailLengthsAvailableDescending = Object.keys(
      constants.MOUNTING_RAILS[color]
    )
      .map(approxLength => getPreciseRailLength(approxLength, color))
      .filter(preciseLength => preciseLength)
      .sort((length1, length2) => length2 - length1);

    const optimalRails = getOptimallyCoveringRails(
      preciseRailLengthsAvailableDescending,
      groupWidth
    );
    const optimalRailsTotalLength = lengthOfRails(optimalRails);
    const overflow = optimalRailsTotalLength - groupWidth;
    const firstSection = sectionsInGroup[0];

    const mountingRailsInGroup = [];
    let xOffset = -overflow / 2;
    optimalRails.forEach(preciseRailLength => {
      mountingRailsInGroup.push(
        getPositionedMountingRail(
          firstSection,
          xOffset,
          getRailProductByPreciseLength(preciseRailLength, color)
        )
      );
      xOffset += preciseRailLength;
    });

    const firstSectionInGroup = sectionsInGroup[0];
    const lastSectionInGroup = sectionsInGroup[sectionsInGroup.length - 1];
    groupedMountingRails.push({
      mountingRails: mountingRailsInGroup,
      areaToCoverStartX: firstSectionInGroup.x - postWidth / 2,
      areaToCoverEndX:
        lastSectionInGroup.x + lastSectionInGroup.width + postWidth / 2,
    });
  });

  const wallWidth = geometry.surround(tac.wall.points).width;

  groupedMountingRails.forEach(group => {
    group.mountingRails = getCorrectlyPositionedMountingRails(
      group.mountingRails,
      wallWidth
    );
  });

  return groupedMountingRails;
}

/**
 * Decides what mounting rails to use for a given configuration.
 * @param {object} tac The TAC representing the configuration.
 * @returns {Array} The mounting rails to use (including their positions).
 */
function getMountingRails(tac) {
  const groupedMountingRails = getGroupedMountingRails(tac);
  const mountingRails = [];
  groupedMountingRails.forEach(group => {
    mountingRails.push(...group.mountingRails);
  });

  return mountingRails;
}

export { getGroupedMountingRails as getCuttableMountingRailData };
export default getMountingRails;
